<template>
  <div class="carousel"
       :class="classes"
       @mouseover="onMouseEnter"
       @mouseleave="onMouseLeave">
    <div v-if="slides && slides.length > 0"
         class="carousel__wrapper"
         :style="carouselStyle">
      <slide-transition v-for="(slide, index) in slides"
                        :key="index">
        <div v-show="slideIndex === index">
          <div :style="slideStyle"
               class="carousel__slide">
            <img v-if="slide.imageUrl"
                 :src="`${slide.imageUrl}?height=720`"
                 :srcset="generateSrcSet( slide.imageUrl )"
                 :alt="slide.imageAlt"
                 class="carousel__image">
            <div class="carousel__container container">
              <div class="carousel__content"
                   :style="contentStyle">
                <div class="carousel__text-content">
                  <div v-if="heading"
                       class="carousel__main-heading">
                    {{ heading }}
                  </div>
                  <component :is="headingSize"
                             class="carousel__heading"
                             :class="headingClasses">
                    {{ slide.heading }}
                  </component>
                  <!-- eslint-disable vue/no-v-html -->
                  <div class="carousel__description"
                       v-html="slide.description" />
                  <!-- eslint-enable vue/no-v-html -->

                  <div class="carousel__link-container">
                    <a v-if="slide.linkText && slide.linkUrl"
                       :class="buttonClasses"
                       :href="slide.linkUrl"
                       class="carousel__button btn">
                      {{ slide.linkText }}
                      <font-awesome-icon :icon="['fas', 'chevron-right']">
                      </font-awesome-icon>
                    </a>
                  </div>
                </div>

                <div v-if="slides && slides.length > 1 && slides.length < maxNavDots"
                     class="carousel__navigator">
                  <font-awesome-icon :icon="['fas', 'chevron-left']"
                                     class="carousel__navigator-icon"
                                     @click="clickedLeft"></font-awesome-icon>
                  <div v-for="navIndex in slides.length"
                       :key="navIndex"
                       class="carousel__navigator-slide-dot"
                       :class="{'carousel__navigator-slide-dot--selected':
                         navIndex - 1 === slideIndex}"
                       @click="selectSlide(navIndex - 1)" />
                  <font-awesome-icon :icon="['fas', 'chevron-right']"
                                     class="carousel__navigator-icon"
                                     @click="clickedRight"></font-awesome-icon>
                </div>
              </div>
            </div>
          </div>
        </div>
      </slide-transition>
    </div>
    <progress-ring v-if="autoPlay && slides && slides.length > 1"
                   ref="progressRing"
                   color="white"
                   :class="'progress-ring--' + internalImageAlign"
                   :radius="24"
                   :thickness="3"
                   :paused="progressedPaused"
                   :animation-duration="autoPlayDuration"
                   pause-icon>
    </progress-ring>
  </div>
</template>

<script>
import SlideTransition from "~/Components/Base/SlideTransition.vue";
import ProgressRing from "~/Components/Base/ProgressRing.vue";
import RecurringTimer from "~/Scripts/modules/timer";

export default {
  name: "HeroCarousel",
  components: {
    SlideTransition,
    ProgressRing,
  },
  props: {
    heading: {
      type: String,
      default: null,
    },
    headingStyle: {
      type: String,
      default: "bold",
    },
    headingSize: {
      type: String,
      default: "h2",
    },
    imageAlign: {
      type: String,
      default: "left",
    },
    buttonStyle: {
      type: String,
      default: "white",
    },
    slides: {
      type: Array,
      default: null,
    },
    buttonClass: {
      type: String,
      default: null,
    },
    gradientColor: {
      type: String,
      default: "#fff",
    },
    fullWidth: {
      type: Boolean,
      defautlt: true,
    },
    height: {
      type: String,
      default: "712px",
    },
    maxNavDots: {
      type: Number,
      default: 7,
    },
    autoPlay: {
      type: Boolean,
      default: true,
    },
    autoPlayDuration: {
      type: Number,
      default: 5000,
    },
  },
  data () {
    return {
      slideIndex: 0,
      images: [],
      intervalInstance: null,
      progressedPaused: false,
      mouseOverCarousel: false,
    };
  },
  computed: {
    internalImageAlign () {
      return this.imageAlign.trim().toLowerCase();
    },
    currentSlideAlias () {
      return this.getSlideStateAlias( this.slideIndex );
    },
    currentSlide () {
      return this.slides[this.slideIndex];
    },
    slideStyle () {
      return {
        "background-image": `linear-gradient( to ${this.internalImageAlign},
         ${this.gradientColor} 0%, rgba( 0, 0, 0, 0 ) 60% )`,
        "min-height": this.height,
      };
    },
    classes () {
      const className = `carousel--${this.internalImageAlign}`;

      return {
        "carousel--full-width": this.fullWidth,
        [className]: true,
      };
    },
    headingClasses () {
      return {
        "carousel__heading--bold": this.headingStyle === "bold",
      };
    },
    buttonClasses () {
      return {
        "carousel__button--red": this.buttonStyle === "red",
        "carousel__button--white": this.buttonStyle === "white",
        "carousel__button--link": this.buttonStyle === "link",
      };
    },
    contentStyle () {
      let result = null;

      if ( this.internalImageAlign === "left" ) {
        result = {
          "margin-left": "auto",
        };
      }

      return result;
    },
    carouselStyle () {
      return {
        "background-color": `${this.gradientColor}`,
      };
    },
    progressRing () {
      let result = null;

      if ( this.$refs.progressRing ) {
        result = this.$refs.progressRing;
      }

      return result;
    },
  },
  watch: {
    autoPlay: {
      immediate: true,
      handler ( val ) {
        if ( val ) {
          this.intervalInstance = new RecurringTimer( this.slideRight, this.autoPlayDuration );
        } else if ( this.intervalInstance ) {
          this.intervalInstance.clear();
        }
      },
    },
  },
  mounted () {
    window.addEventListener( "focus", this.resetAutoplay );
  },
  created () {
    if ( this.slides && this.slides.length ) {
      // Pre-load images
      for ( let i = 0; i < this.slides.length; i += 1 ) {
        const slide = this.slides[i];

        if ( slide.imageUrl ) {
          const image = new Image();
          image.src = slide.imageUrl;
          this.images.push( image );
        }
      }
    }
  },
  destroyed () {
    window.removeEventListener( "focus", this.resetAutoplay );
  },
  methods: {
    toggleAutoPlay () {
      this.autoPlay = !this.autoPlay;
    },
    clickedLeft () {
      this.slideLeft();
      // Reset the autoplay interval and progress animation to keep sync.
      if ( this.progressRing ) {
        this.progressRing.resetAnimation();
      }
      this.resetAutoplay();
    },
    clickedRight () {
      this.slideRight();
      // Reset the autoplay interval and progress animation to keep sync.
      if ( this.progressRing ) {
        this.progressRing.resetAnimation();
      }
      this.resetAutoplay();
    },
    slideLeft () {
      if ( this.slideIndex - 1 < 0 ) {
        this.selectSlide( this.slides.length - 1 );
      } else {
        this.selectSlide( this.slideIndex - 1 );
      }
    },
    slideRight () {
      if ( this.slideIndex + 1 > this.slides.length - 1 ) {
        this.selectSlide( 0 );
      } else {
        this.selectSlide( this.slideIndex + 1 );
      }
    },
    selectSlide ( index ) {
      this.slideIndex = index;
    },
    getSlideStateAlias ( index ) {
      return `slide_${index}`;
    },
    onMouseEnter () {
      this.progressedPaused = true;
      this.mouseOverCarousel = true;
      if ( this.intervalInstance ) {
        this.intervalInstance.pause();
      }
    },
    onMouseLeave () {
      this.progressedPaused = false;
      this.mouseOverCarousel = false;
      if ( this.intervalInstance ) {
        this.intervalInstance.resume();
      }
    },
    resetAutoplay () {
      // Reset the autoplay slide interval to keep sync with the progress animation.
      if ( this.autoPlay && this.intervalInstance ) {
        this.intervalInstance.clear();
        this.intervalInstance = new RecurringTimer( this.autoMoveSlide, this.autoPlayDuration );
        if ( this.mouseOverCarousel ) {
          this.progressedPaused = true;
        }
      }
    },
    autoMoveSlide () {
      this.slideRight();
      if ( this.progressRing ) {
        this.progressRing.resetAnimation();
      }
    },
    generateSrcSet ( imageUrl ) {
      const result = [];

      result.push( `${imageUrl}?height=720 1x` );
      result.push( `${imageUrl}?height=1440 2x` );

      return result.join( ", " );
    },
  },
};
</script>

<style lang="less" scoped>
.carousel {
  max-width: 1500px;
  margin: 0 auto;
  position: relative;

  &__wrapper {
    z-index: 2;
    position: relative;
  }

  &--full-width {
    max-width: inherit;
  }

  &__main-heading {
    color: #D71920;
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 20px;

    @media ( max-width: 992px ) {
      font-size: 19px;
      margin-bottom: 10px;
    }
  }

  &__slide {
    background-size: cover;
    display: flex;
    width: 100%;

    @media ( max-width: 991px ) {
      background-color: rgba( 255, 255, 255, 0.7 );
      background-image: none !important;
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -1;
    position: absolute;
  }

  @media ( max-width: 991px ) {
    &--right {
      .carousel__image {
        object-position: 75% 50%;
      }
    }

    &--left {
      .carousel__image {
        object-position: 25% 50%;
      }
    }
  }

  &__container {
    display: flex;
  }

  &__text-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    margin-bottom: 20px;

    @media ( max-width: 992px ) {
      margin-bottom: 10px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex-basis: 40%;
    padding: 40px 0;

    @media ( max-width: 992px ) {
      flex-basis: 100%;
    }
  }

  &__heading {
    margin-bottom: 20px;

    @media ( max-width: 992px ) {
      margin-bottom: 10px;
    }

    &--normal {
      font-weight: 400;
    }

    &--bold {
      font-weight: 500;
    }
  }

  &__description {
    font-size: 18px;
    line-height: 24px;
    margin-bottom: 20px;

    @media ( max-width: 992px ) {
      font-size: 17px;
      margin-bottom: 10px;
    }
  }

  &__button {
    text-decoration: none;
    box-shadow: 0 0 10px 0 rgba( 0, 0, 0, 0.2 );

    &:hover {
      text-decoration: none;
    }

    &--red {
      color: #fff !important;
      background-color: #D71920;

      &:hover {
        border: 2px solid #fff;
      }
    }

    &--white {
      color: #D71920 !important;
      background-color: #fff;

      &:hover {
        border: 2px solid #D71920;
      }
    }

    &--link {
      color: #D71920 !important;
      box-shadow: none;
      font-weight: 400;
      padding-left: 0;
      padding-right: 0;
    }
  }

  &__timer {
    position: absolute;
  }

  &__navigator {
    margin-top: auto;
    display: flex;
    flex-direction: row;
    align-items: center;

    &-icon {
      font-size: 18px;

      &:first-child {
        margin-right: 20px;
      }

      &:last-child {
        margin-left: 20px;
      }

      &:hover {
        cursor: pointer;
      }
    }

    &-slide-dot {
      width: 20px;
      height: 20px;
      border: 2px solid lightgray;
      border-radius: 10px;
      margin: 0 10px;
      transition: background-color .2s ease-out, border-color .2s ease-out;

      &--selected {
        background-color: #333;
        border-color: #333;
      }

      &:hover {
        cursor: pointer;
      }
    }
  }

  .progress-ring {
    position: absolute;
    margin: 10px;
    top: 0;
    z-index: 10000;

    &--right {
      right: 0;
    }

    &--left {
      left: 0;
    }
  }
}
</style>
